import { httpMessage } from "@saleshub/dictionary/http";
import { z } from "zod";
import { withId } from "../../utils/with-id.js";
import { phoneSchema } from "../phones/phones.schema.js";
import { rolesSchemaWithId } from "../roles/roles.schema.js";
const USER_ROLE = {
    PROPERTY_ADMIN: "property admin",
    SALESPERSON: "salesperson",
    COACH_ADMIN: "coach admin",
    ORG_ADMIN: "account admin",
    HYBRID_USER: "hybrid user",
};
export const USER_STATUS = {
    ACTIVE: "active",
    PENDING: "pending",
    INACTIVE: "inactive",
};
export const USER_NAME_MAX_LENGTH = 240;
export const USER_NAME_REGEX = /^[\p{L} \-']+$/u;
export const userNameSchema = z
    .string({
    required_error: httpMessage.responses.users.required.name,
    invalid_type_error: httpMessage.responses.users.invalid.name,
})
    .max(USER_NAME_MAX_LENGTH, httpMessage.responses.users.tooLong.name)
    .regex(USER_NAME_REGEX, httpMessage.responses.users.invalid.name)
    .trim();
export const usersSchema = z.object({
    roleId: z.number().int(),
    role: rolesSchemaWithId.optional(),
    name: userNameSchema,
    email: z
        .string({
        required_error: httpMessage.responses.users.required.email,
    })
        .email({
        message: httpMessage.responses.users.invalid.email,
    })
        .trim(),
    photoUrl: z
        .string({
        required_error: httpMessage.responses.users.required.photoUrl,
    })
        .url({
        message: httpMessage.responses.users.invalid.photoUrl,
    })
        .trim(),
    originalPhotoUrl: z
        .string({
        required_error: httpMessage.responses.users.required.originalPhotoUrl,
    })
        .url({
        message: httpMessage.responses.users.invalid.originalPhotoUrl,
    })
        .trim(),
    photoSettings: z.record(z.any(), {
        required_error: httpMessage.responses.users.required.photoSettings,
        invalid_type_error: httpMessage.responses.users.invalid.photoSettings,
    }),
    darkModeEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.darkModeEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.darkModeEnabled,
    }),
    contentNotificationsEmailEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.contentNotificationsEmailEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.contentNotificationsEmailEnabled,
    }),
    contentNotificationsSmsEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.contentNotificationsSmsEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.contentNotificationsSmsEnabled,
    }),
    engagementNotificationsEmailEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.engagementNotificationsEmailEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.engagementNotificationsEmailEnabled,
    }),
    engagementNotificationsSmsEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.engagementNotificationsSmsEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.engagementNotificationsSmsEnabled,
    }),
    generalNotificationsEmailEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.generalNotificationsEmailEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.generalNotificationsEmailEnabled,
    }),
    generalNotificationsSmsEnabled: z.boolean({
        required_error: httpMessage.responses.users.required.generalNotificationsSmsEnabled,
        invalid_type_error: httpMessage.responses.users.invalid.generalNotificationsSmsEnabled,
    }),
    permissions: z
        .array(z.string(), {
        invalid_type_error: httpMessage.responses.users.invalid.permissions,
    })
        .optional(),
    status: z
        .nativeEnum(USER_STATUS, {
        required_error: httpMessage.responses.users.required.status,
        invalid_type_error: httpMessage.responses.users.invalid.status,
    })
        .optional(),
});
export const usersSchemaWithId = usersSchema.merge(withId({
    required_error: httpMessage.responses.users.required.id,
    invalid_type_error: httpMessage.responses.users.invalid.id,
}));
const addUserBaseFields = {
    name: usersSchema.shape.name,
    email: usersSchema.shape.email,
    workPhone: phoneSchema.optional(),
};
export const createAddUserSchema = (allowedRoles) => {
    return z.object({
        ...addUserBaseFields,
        role: z.enum(allowedRoles, {
            required_error: httpMessage.responses.users.required.role,
            invalid_type_error: httpMessage.responses.users.invalid.role,
        }),
    });
};
export const addMemberSchema = createAddUserSchema([USER_ROLE.SALESPERSON, USER_ROLE.PROPERTY_ADMIN]);
export const addOrganizationUserSchema = createAddUserSchema([USER_ROLE.ORG_ADMIN]);
export const partialUserSchema = usersSchema.partial();
